import { managementDraft, readManagement, managementSaved } from '../utils/managementDraft';
import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, FormControlLabel, Switch, Checkbox, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faCircleCheck, faSpinner } from "@fortawesome/free-solid-svg-icons";
import "./reporting.css";
import { useTranslation, Trans } from "react-i18next";
import "../translations.js";
import { guardWrite } from "../utils/permissions";

const ALLOWED = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const ALIASES = { mo: "mon", tu: "tue", we: "wed", th: "thu", fr: "fri", sa: "sat", su: "sun" };
const REPORT_BASE_TIMEZONE = "Europe/Berlin";
const REPORT_HOUR = 8;
const REPORT_MINUTE = 0;

function safeParseJson(value, fallback) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

function normalizeWeekdays(list) {
  const out = [];
  (list || []).forEach((k) => {
    if (!k || typeof k !== "string") return;
    const lower = k.toLowerCase();
    const mapped = ALIASES[lower] || lower;
    if (ALLOWED.includes(mapped) && !out.includes(mapped)) out.push(mapped);
  });
  return out;
}

function getUserTimezone() {
  return localStorage.getItem("timezone") || "UTC";
}

function getNextReportTime(t) {
  const userTimezone = getUserTimezone();
  try {
    const now = new Date();
    const baseDateParts = new Intl.DateTimeFormat("en-CA", {
      timeZone: REPORT_BASE_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const part = (type) => baseDateParts.find((p) => p.type === type)?.value;
    const reportDate = new Date(
      `${part("year")}-${part("month")}-${part("day")}T${String(REPORT_HOUR).padStart(2, "0")}:${String(REPORT_MINUTE).padStart(2, "0")}:00+02:00`
    );
    const time = new Intl.DateTimeFormat(undefined, {
      timeZone: userTimezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    }).format(reportDate);
    return { time, timezone: userTimezone };
  } catch {
    return { time: t("reporting_fallback_time") || "8:00 AM", timezone: userTimezone };
  }
}

const Reporting = ({ botId }) => {
  const { t } = useTranslation();
  const management = useRef(managementDraft());
  const [loading, setLoading] = useState(true);
  const [isReportingActive, setIsReportingActive] = useState(false);
  const [includeExcel, setIncludeExcel] = useState(false);
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [emails, setEmails] = useState([""]);
  const [initialIsReportingActive, setInitialIsReportingActive] = useState(false);
  const [initialIncludeExcel, setInitialIncludeExcel] = useState(false);
  const [initialSelectedWeekdays, setInitialSelectedWeekdays] = useState([]);
  const [initialEmails, setInitialEmails] = useState([""]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const suppressUnsavedCheck = useRef(false);
  const WEEKDAYS = [
    { key: "mon", label: t("monday") || "Monday" },
    { key: "tue", label: t("tuesday") || "Tuesday" },
    { key: "wed", label: t("wednesday") || "Wednesday" },
    { key: "thu", label: t("thursday") || "Thursday" },
    { key: "fri", label: t("friday") || "Friday" },
    { key: "sat", label: t("saturday") || "Saturday" },
    { key: "sun", label: t("sunday") || "Sunday" },
  ];

  const fetchReportingSettings = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await readManagement(fetch, 'reports', id, 'reporting_settings');
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const data = await res.json();
      management.current.loaded(id, data);

      const active = Boolean(data.active);
      const excel = Boolean(data.include_excel);
      const weekdaysRaw = safeParseJson(data.weekdays, []);
      const emailsRaw = safeParseJson(data.email, []);
      const weekdays = normalizeWeekdays(weekdaysRaw);
      const emailsList = (emailsRaw.length ? emailsRaw : [""]).map(String);

      setIsReportingActive(active);
      setIncludeExcel(excel);
      setSelectedWeekdays(weekdays);
      setEmails(emailsList);

      setInitialIsReportingActive(active);
      setInitialIncludeExcel(excel);
      setInitialSelectedWeekdays(weekdays);
      setInitialEmails(emailsList);

      setHasLoadedInitial(true);
    } catch (err) {
      console.error("Error fetching reporting settings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (botId) fetchReportingSettings(botId);
  }, [botId, fetchReportingSettings]);

  useEffect(() => {
    if (!hasLoadedInitial || suppressUnsavedCheck.current) return;

    const changed =
      initialIsReportingActive !== isReportingActive ||
      initialIncludeExcel !== includeExcel ||
      JSON.stringify(initialSelectedWeekdays) !== JSON.stringify(selectedWeekdays) ||
      JSON.stringify(initialEmails) !== JSON.stringify(emails);

    setHasUnsavedChanges(changed);
  }, [
    isReportingActive,
    includeExcel,
    selectedWeekdays,
    emails,
    initialIsReportingActive,
    initialIncludeExcel,
    initialSelectedWeekdays,
    initialEmails,
    hasLoadedInitial,
  ]);

  const handleToggleReporting = async (active) => {
    // Read-only teammates: don't visually flip the switch — surface the
    // shared permission modal and bail before changing local state so the
    // toggle doesn't appear to succeed and then revert.
    if (!guardWrite()) return;
    suppressUnsavedCheck.current = true;
    setIsReportingActive(active);
    try {
      const payload = { active };
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}reporting_settings/${botId}`, management.current.request(botId, payload));
      await managementSaved(res, management.current, botId, t);

      setInitialIsReportingActive(active);
    } catch (err) {
      setIsReportingActive(initialIsReportingActive);
      console.error("Error toggling reporting activation:", err);
    } finally {
      setTimeout(() => {
        suppressUnsavedCheck.current = false;
      }, 100);
    }
  };

  const toggleWeekday = (key) => {
    setSelectedWeekdays((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleEmailChange = (index, value) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
  };

  const handleAddEmail = () => setEmails((prev) => [...prev, ""]);
  const handleRemoveEmail = (index) => setEmails((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!guardWrite(handleDiscard)) return;
    try {
      setLoading(true);
      const payload = {
        active: isReportingActive,
        include_excel: includeExcel,
        weekdays: normalizeWeekdays(selectedWeekdays),
        email: emails.map(String).map(v => v.trim()).filter(Boolean),
      };
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}reporting_settings/${botId}`, management.current.request(botId, payload));
      await managementSaved(res, management.current, botId, t);

      setInitialIsReportingActive(isReportingActive);
      setInitialIncludeExcel(includeExcel);
      setInitialSelectedWeekdays(payload.weekdays);
      setInitialEmails(payload.email);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving reporting settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    setIsReportingActive(initialIsReportingActive);
    setIncludeExcel(initialIncludeExcel);
    setSelectedWeekdays([...initialSelectedWeekdays]);
    setEmails([...initialEmails]);
  };

  const formatSelectedDays = () => {
    const dayMap = {
      mon: t("monday") || "Monday",
      tue: t("tuesday") || "Tuesday",
      wed: t("wednesday") || "Wednesday",
      thu: t("thursday") || "Thursday",
      fri: t("friday") || "Friday",
      sat: t("saturday") || "Saturday",
      sun: t("sunday") || "Sunday",
    };
    const days = selectedWeekdays.map((d) => dayMap[d]).filter(Boolean);
    if (!days.length) return "";
    if (days.length === 1) return days[0];
    return `${days.slice(0, -1).join(", ")} ${t("reporting_days_conjunction") || "and"} ${days.slice(-1)}`;
  };

  const nextReportTime = getNextReportTime(t);

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="reporting-parent">
      {/* Activate Reporting */}
      <Box className="report-control-group">
        <FormControlLabel
          control={
            <Switch
              checked={isReportingActive}
              onChange={(e) => handleToggleReporting(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#5616ea" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#5616ea" },
                "& .MuiSwitch-track": { backgroundColor: "#ccc" },
              }}
            />
          }
          label={t("activate_reporting") || "Activate reporting"}
        />
      </Box>

      {/* Emails */}
      <Box className="report-control-group">
        <Typography variant="h3" component="h3" className="report-section-title" style={{ marginBottom: "20px" }}>
          {t("notification-receiver") || "Notification receiver"}
        </Typography>

        {emails.map((email, index) => (
          <Box key={index} display="flex" alignItems="center" marginBottom={1}>
            <TextField
              label={`${t("email") || "Email"} ${index + 1}`}
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(index, e.target.value)}
              fullWidth
              variant="outlined"
              autoComplete="email"
              className="warm-lead-email-receiver"
              disabled={!isReportingActive}
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused": {
                  "& fieldset": { border: "1px solid #515151" },
                },
                "& .MuiInputLabel-root": {
                  color: "gray",
                  "&.Mui-focused": { color: "#5616ea" },
                },
              }}
            />
            {emails.length > 1 && (
              <button
                onClick={() => handleRemoveEmail(index)}
                className="remove-email-button"
                title={t("remove_email") || "Remove email"}
                disabled={!isReportingActive}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </Box>
        ))}

        <button
          onClick={handleAddEmail}
          className="add-email-button"
          title={t("add_email") || "Add email"}
          disabled={!isReportingActive}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </Box>

      {/* Weekdays */}
      <Box className="report-control-group">
        <Typography variant="h3" component="h3" className="report-section-title" style={{ marginBottom: "10px" }}>
          {t("schedule") || "Schedule"}
        </Typography>

        <div
          className={`report-weekdays-column ${!isReportingActive ? "is-disabled" : ""}`}
        >
          {WEEKDAYS.map((w) => (
            <label key={w.key} className="report-weekday-item">
              <Checkbox
                checked={selectedWeekdays.includes(w.key)}
                onChange={() => toggleWeekday(w.key)}
                disabled={!isReportingActive}
                sx={{
                  "&.Mui-checked": { color: "#5616ea", backgroundColor: "transparent" },
                  "&.Mui-checked:hover": { backgroundColor: "#5616ea15" },
                  "&:hover": { backgroundColor: "#5616ea15" },

                  /* make the tick and box look muted when disabled */
                  "&.Mui-disabled": { color: "#b0b0b0" },
                  "&.Mui-checked.Mui-disabled": { color: "#b0b0b0" },
                }}
              />
              <span className={!isReportingActive ? "weekday-label-disabled" : ""}>
                {w.label}
              </span>
            </label>
          ))}
        </div>
      </Box>

      {/* Include Excel */}
      <Box className="report-control-group">
        <FormControlLabel
          control={
            <Switch
              checked={includeExcel}
              onChange={(e) => setIncludeExcel(e.target.checked)}
              disabled={!isReportingActive}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#5616ea" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#5616ea" },
                "& .MuiSwitch-track": { backgroundColor: "#ccc" },
              }}
            />
          }
          label={t("include_chat_history_excel") || "Include chat history (Excel)"}
        />
      </Box>

      {/* Unsaved changes banner */}
      {hasUnsavedChanges && (
        <div className="report-unsaved-banner">
          <div className="report-unsaved-icon">⚠️</div>
          <div className="report-unsaved-text">{t("unsaved_changes_info") || "You have unsaved changes."}</div>
        </div>
      )}

      {/* Actions */}
      <div className="report-actions">
        <button className="report-save-btn" onClick={handleSave} disabled={!isReportingActive}>
          {t("save") || "Save"}
        </button>
        {hasUnsavedChanges && (
          <button className="report-discard-btn" onClick={handleDiscard}>
            {t("discard_changes") || "Discard changes"}
          </button>
        )}
      </div>

      {/* Next report info box */}
      {isReportingActive && selectedWeekdays.length > 0 && (
        <div className="next-report-info-box" style={{ marginTop: "5px" }}>
          <FontAwesomeIcon className="next-report-info-icon" icon={faCircleCheck} />
          <div className="report-info-content">
            <p>
              <Trans
                i18nKey="reporting_next_report_html"
                values={{
                  days: formatSelectedDays(),
                  time: nextReportTime.time,
                  timezone: nextReportTime.timezone,
                }}
                components={{ strong: <strong /> }}
              >
                You’ll receive an <strong>analytics report</strong> every <strong>{"{{days}}"}</strong> at <strong>{"{{time}}"}</strong> in your timezone <strong>({"{{timezone}}"})</strong>.
              </Trans>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reporting;
